import React, { useRef } from 'react';
import { observer } from 'mobx-react';
import InspectorTabs from './InspectorTabs';
import { RSButton } from 'reactsymbols-kit';
import { Row, Col } from '../Grid';
import AttributesInspector from './AttributesInspector';
import EditMethodController from '../../merode/controllers/EditMethodController';
import { RSInput, RSRadio, RSTextarea } from 'reactsymbols-kit';
import RadioGroup from '../RadioGroup';
import Styles from '../../styles/colors';
import type { IMethod } from '../../stores/model/metamodel/method';
import type { IMethodInspector } from '../../stores/ui/method_inspector';
import type { IModelVersion } from '../../merode/controllers/Controller';
import type Report from '../../merode/checks/Report';
import type { AttributeUpdates, MethodUpdates } from '../../merode/commands/types';
import {
  asRSChange,
  rsValue,
  type RSInputChange,
  type RSTextareaChange,
} from './rsForm';

interface MethodInspectorProps {
  /** The ModelVersion (to pass to the controller). */
  version: IModelVersion;
  /** The method to inspect. */
  method: IMethod;
  /** The MethodInspector ui store. */
  ui: IMethodInspector;
  /** Callback to call when a report is generated. */
  onReport?: (report: Report) => void;
  /** Callback to close the inspector. */
  requestClose?: () => void;
}

/**
 * Method inspector.
 *
 * Props:
 *
 * - version: The ModelVersion (to pass to the controller)
 * - method: the method to inspect
 * - ui: the MethodInspector store
 * - onReport: callback to call when a report is generated.
 * - requestClose: callback to close the inspector.
 *
 * Unlike the object/event/dependency inspectors, this one has no
 * getDerivedStateFromProps and no discard-on-swap: it is NOT key-remounted in
 * Inspector/index.js, preserving the class behavior where the instance (and its
 * controller) persisted across method selections and relied entirely on
 * ProjectVersion.confirmable to discard pending edits.
 */
const MethodInspector = observer(function MethodInspector({
  version,
  method,
  ui,
  onReport,
  requestClose,
}: MethodInspectorProps) {
  // The controller is created once per mount (constructor instance field).
  const controllerRef = useRef<EditMethodController | null>(null);
  if (controllerRef.current === null) {
    controllerRef.current = new EditMethodController(version);
  }
  const controller = controllerRef.current;

  const { changes } = ui;

  /**
   * Utitility helpers to:
   * - get the method properties either from the changes or from the method.
   * - check whether a property changed.
   */
  const name = changes.name !== null ? changes.name : method.name;
  const nameChanged = changes.name !== null;
  const type = changes.type !== null ? changes.type : method.type;
  const precondition =
    changes.precondition !== null ? changes.precondition : method.precondition;
  const preconditionChanged = changes.precondition !== null;
  const postcondition =
    changes.postcondition !== null ? changes.postcondition : method.postcondition;
  const postconditionChanged = changes.postcondition !== null;
  const implementation =
    changes.implementation !== null ? changes.implementation : method.implementation;
  const implementationChanged = changes.implementation !== null;

  /**
   * Get the updates to the attributes in the format that the controller
   * expects.
   */
  // The store's `edits`/`additions` views type each property `string | null`,
  // but the form returns real values for the dirty entries, satisfying the
  // command's `AttributeUpdates`. Cast at this store→command boundary.
  const attributeUpdates = (): AttributeUpdates => {
    const { attributesInspector } = ui;
    return {
      removals: attributesInspector.removals,
      edits: attributesInspector.edits,
      additions: attributesInspector.additions,
    } as unknown as AttributeUpdates;
  };

  const discardChanges = () => {
    ui.discardChanges();
  };

  const applyChanges = async () => {
    ui.setProjectBusy(true);
    // `type` comes from the pending change as a plain `string`; the radio only
    // produces the three valid method types, matching the command's enum. Cast
    // to the command's `MethodType` (`MethodUpdates['type']`) at this boundary.
    const report = await controller.editMethod(method.id, {
      name,
      type: type as MethodUpdates['type'],
      precondition,
      implementation,
      postcondition,
      attributes: attributeUpdates(),
    });
    if (report.successful) {
      discardChanges();
    }
    ui.setProjectBusy(false);
    if (onReport) onReport(report);
  };

  const deleteMethod = async () => {
    ui.setProjectBusy(true);
    const report = await controller.deleteMethod(method.id);
    if (requestClose) requestClose();
    ui.setProjectBusy(false);
    if (onReport) onReport(report);
  };

  // Render the general tab
  const renderGeneral = () => {
    const check = controller.checkNameInput(name);
    return (
      <React.Fragment>
        <RSInput
          label="Method name"
          value={name}
          onChange={asRSChange<RSInputChange>((value: string) =>
            changes.storeChange('name', method.name, value)
          )}
          className={nameChanged ? 'dirty' : ''}
          level={rsValue(check.ok ? null : 'danger')}
          text={rsValue(check.ok ? null : check.message)}
        />

        <dl className="method-attrs mb-[5px] text-[14px] last:mb-0">
          <dt className="float-left font-medium">Provenance:</dt>
          <dd className="mb-[5px] ml-[105px] last:mb-0">{method.provenance}</dd>
          <dt className="float-left font-medium">For object:</dt>
          <dd className="mb-[5px] ml-[105px] last:mb-0">{method.ownerObjectType.name}</dd>
          <dt className="float-left font-medium">For event:</dt>
          <dd className="mb-[5px] ml-[105px] last:mb-0">{method.ownerEventType.name}</dd>
          {method.viaMethod && (
            <React.Fragment>
              <dt className="float-left font-medium">Via method:</dt>
              <dd className="mb-[5px] ml-[105px] last:mb-0">{method.viaMethod.asString()}</dd>
            </React.Fragment>
          )}

          {method.viaDependency && (
            <React.Fragment>
              <dt className="float-left font-medium">Via dependency:</dt>
              <dd className="mb-[5px] ml-[105px] last:mb-0">{method.viaDependency.displayName}</dd>
            </React.Fragment>
          )}

          {method.viaInheritance && (
            <React.Fragment>
              <dt className="float-left font-medium">Via inheritance:</dt>
              <dd className="mb-[5px] ml-[105px] last:mb-0">{method.viaInheritance.utf8DisplayString}</dd>
            </React.Fragment>
          )}

          <dt className="method-type float-left mt-[7px] font-medium">Type:</dt>
          <dd className="method-type mb-[5px] mt-[15px] ml-[60px] last:mb-0">
            <RadioGroup
              key={
                method.id /* Work-around for bug in RSRadioGroup: make sure it is swapped by a new component when the method changes */
              }
              selectedValue={type}
              name="radio-group"
              onChange={(value) => changes.storeChange('type', method.type, value)}
            >
              <RSRadio value="CREATE" label="Create" />
              <RSRadio value="MODIFY" label="Modify" />
              <RSRadio value="END" label="End" />
            </RadioGroup>
          </dd>
        </dl>
      </React.Fragment>
    );
  };

  // Render the conditions and implementation tab
  const renderConditionsImplementation = () => {
    return (
      <React.Fragment>
        <RSTextarea
          label="Precondition"
          value={precondition}
          onChange={asRSChange<RSTextareaChange>((value: string) =>
            changes.storeChange('precondition', method.precondition, value)
          )}
          className={preconditionChanged ? 'dirty' : ''}
        />
        <RSTextarea
          label="Implementation"
          value={implementation}
          onChange={asRSChange<RSTextareaChange>((value: string) =>
            changes.storeChange('implementation', method.implementation, value)
          )}
          className={implementationChanged ? 'dirty' : ''}
        />
        <RSTextarea
          label="Postcondition"
          value={postcondition}
          onChange={asRSChange<RSTextareaChange>((value: string) =>
            changes.storeChange('postcondition', method.postcondition, value)
          )}
          className={postconditionChanged ? 'dirty' : ''}
        />
      </React.Fragment>
    );
  };

  // Render the attributes tab
  const renderAttributes = () => {
    return (
      <AttributesInspector
        attributes={method.attributes}
        ui={ui.attributesInspector}
        controller={controller}
        showTitle={false}
      />
    );
  };

  // Configuration for the InspectorTabs component
  const tabs = () => {
    return [
      {
        key: 'general',
        title: 'General',
        content: renderGeneral(),
      },
      {
        key: 'conditions-implementation',
        title: 'Conditions & implementation',
        content: renderConditionsImplementation(),
      },
      {
        key: 'attributes',
        title: 'Attributes',
        content: renderAttributes(),
      },
    ];
  };

  const renderButtons = () => {
    if (ui.dirty) {
      return (
        <div className="editMethod p-2.5">
          <Row gutter={5}>
            <Col md={6}>
              <RSButton
                type="button"
                background={Styles.defaultGradient}
                color={Styles.darkTextColor}
                value="Discard"
                onClick={discardChanges}
              />
            </Col>
            <Col md={6}>
              <RSButton
                type="submit"
                background={Styles.blueGradient}
                value="Apply changes"
                onClick={applyChanges}
              />
            </Col>
          </Row>
        </div>
      );
    } else {
      return (
        <div className="removeMethod p-2.5">
          <RSButton
            type="button"
            background={Styles.redGradient}
            value="Delete method"
            onClick={deleteMethod}
          />
        </div>
      );
    }
  };

  return (
    <div className="method-inspector flex h-full flex-col">
      <InspectorTabs tabs={tabs()} />
      {renderButtons()}
    </div>
  );
});

export default MethodInspector;
